import dayjs from 'dayjs';
import { map } from 'lodash';
import {
  COLLECTIONS,
  US_TIMESTAMP_FORMAT,
  USER_ACTIONS,
} from 'src/constants/constants';
import { ApiResponse, IUserActions, IWeekdayDaypart } from 'src/interfaces';
import { lang } from 'src/utils';

export const mapUserActions = (
  response: ApiResponse,
  weeksDayparts: IWeekdayDaypart[],
) => {
  const filteredData = response.data.filter((item: IUserActions) => {
    // Filter out changeSets for create, update, and delete operations
    if (
      item.collection === 'changeSets' &&
      (item.operation === 'create' ||
        item.operation === 'update' ||
        item.operation === 'delete')
    ) {
      return false;
    }

    return true;
  });

  const formattedData = map(filteredData, (item: IUserActions) => {
    const timestamp = dayjs(item.timestamp).format(US_TIMESTAMP_FORMAT);
    const description =
      item?.data?.narrative || getDescriptionForChange(item, weeksDayparts);
    const collection = getCollectionName(item);

    return {
      ...item,
      name: item.name ? item.name : (item.record_id as string),
      collection: collection,
      timestamp,
      description,
    };
  });

  return {
    data: formattedData,
    additional_data: response.additional_data,
    totalCount: response.totalCount,
    totalPages: response.totalPages,
  };
};

export const getDescriptionForChange = (
  item: IUserActions,
  weeksDayparts: IWeekdayDaypart[],
): string => {
  const { collection, name, operation, record_id, data, additional_data } =
    item;

  switch (operation) {
    case 'create':
      switch (collection) {
        case 'scheduleDayparts':
          return `${
            weeksDayparts.find(
              (weekDaypart) => weekDaypart.id === data?.weekday_daypart_id,
            )?.daypart.name
          } was created on ${
            data?.location_group_id
              ? `location group ${data.location_group_id}`
              : `location ${data.location_channel_id}`
          }`;

        case 'scheduleAssemblies':
        case 'defaultAssemblies':
          if (
            additional_data?.location_group_name &&
            additional_data?.assembly_name &&
            additional_data?.daypart_name
          ) {
            return `At ${additional_data.location_group_name}, ${additional_data.assembly_name} was scheduled for ${additional_data.daypart_name} daypart.`;
          }

          return data?.location_code
            ? `At location ${data.location_code}, assembly id ${data.assembly_id} was scheduled.`
            : `At location group, assembly id ${data.assembly_id} was scheduled.`;

        case 'scheduleOverrides':
          if (additional_data === null) {
            return `${name ? name : record_id} was created.`;
          }
          if (data?.override_products)
            return `At ${additional_data?.location_nsn}, default lineup override was created on Component: ${additional_data?.component_name}, Id: ${additional_data?.component_id}.`;
          else if (data?.component_id)
            return `At ${additional_data?.location_nsn}, replace component override was created on Component: ${additional_data?.component_name}, Id: ${additional_data?.component_id}. Component: ${additional_data?.new_component_name}, Id: ${data?.component_id} was added.`;
          else if (data?.component_config)
            return `At ${additional_data?.location_nsn}, COD override was created on Component: ${additional_data?.component_name}, Id: ${additional_data?.component_id}.`;
          else return `${name ? name : record_id} was created.`;
        case 'scheduleDaypartOverrides':
          if (additional_data === null) {
            return `${name ? name : record_id} was created.`;
          }

          return `At ${additional_data?.location_nsn}, ${additional_data?.daypart_name} override was created.`;
        case 'layouts':
          return `Layout ${name ? name : record_id} was created.`;
        case 'screens':
          return `Screen ${record_id} was created.`;
        case 'sections':
          return `Section ${record_id} was created.`;
        case 'screenSections':
          return `Section ${data?.section_id} was added to screen ${data?.screen_id}.`;
        case 'layoutScreens':
          if (additional_data === null) {
            return `Screen ${data?.screen_id} was added to layout.`;
          }

          return `Screen ${data?.screen_id} was added to layout ${additional_data?.layout_name}.`;
        case 'scheduleItemOverrides':
          return `Override with id ${record_id} was created.`;
        default:
          if (data?.clone) {
            return `${name ? name : record_id} was created by duplicating ${
              data.clone
            }.`;
          }

          return `${name ? name : record_id} was created.`;
      }
    case 'update':
      switch (collection) {
        case 'projectConfigs':
          return `${
            record_id === 'scaleTypes'
              ? 'Accessibility settings have been updated'
              : `${record_id
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/^./, (str) => str.toUpperCase())
                  .trim()} have been updated`
          } `;
        case 'locations':
          if (data?.location_group_id && !data?.location_tag_id)
            return `${record_id} was added to ${
              data.location_group_id.length > 1
                ? `location groups with ids ${data.location_group_id}`
                : `location group with id ${data.location_group_id}`
            } `;
          else return `${name ? name : record_id} was updated.`;
        case 'scheduleDaypartOverrides':
          if (additional_data === null) {
            return `${name ? name : record_id} was updated.`;
          }

          return `At ${additional_data?.location_nsn}, ${additional_data?.daypart_name} override was updated.`;
        case 'layouts':
          return `Layout ${name ? name : record_id} was updated.`;
        case 'screens':
          return `Screen ${record_id} was updated.`;
        case 'sections':
          return `Section ${record_id} was updated.`;
        case 'screenSections':
          return `Section was updated for screen.`;
        case 'layoutScreens':
          return `Screen was updated for layout.`;

        default:
          return `${name ? name : record_id} was updated.`;
      }

    case 'delete':
      switch (collection) {
        case 'scheduleOverrides':
          if (additional_data === null) {
            return `${name ? name : record_id} was deleted.`;
          }
          if (data?.override_products)
            return `At ${additional_data?.location_nsn}, default lineup override was deleted on Component: ${additional_data?.component_name}, Id: ${additional_data?.component_id}.`;
          else if (data?.component_id)
            return `At ${additional_data?.location_nsn}, Component: ${additional_data?.new_component_name}, Id: ${data?.component_id} was deleted as an override.`;
          else if (data?.component_config)
            return `At ${additional_data?.location_nsn}, COD override was deleted on Component: ${additional_data?.component_name}, Id: ${additional_data?.component_id}.`;
          else return `${name ? name : record_id} was deleted.`;
        case 'layouts':
          return `Layout ${name ? name : record_id} was deleted.`;
        case 'screens':
          return `Screen ${record_id} was deleted.`;
        case 'sections':
          return `Section ${record_id} was deleted.`;
        case 'screenSections':
          return `Section ${data?.section_id} was removed from screen ${data?.screen_id}.`;
        case 'layoutScreens':
          if (additional_data === null) {
            return `Screen ${data?.screen_id} was removed from layout.`;
          }

          return `Screen ${data?.screen_id} was removed from layout ${additional_data?.layout_name}.`;

        case 'scheduleItemOverrides':
          return `Override with id ${record_id} was deleted.`;
        default:
          return `${name ? name : record_id} was deleted.`;
      }

    default:
      return `Unknown operation for ${collection}.`;
  }
};

export const getCollectionName = (item: IUserActions): string => {
  const { collection } = item;

  switch (collection) {
    case 'locationGroups':
    case 'scheduleDayparts':
    case 'locations':
    case 'locationChannels':
    case 'scheduleAssemblies':
    case 'defaultAssemblies':
    case 'locationTags':
    case 'locationOwnerTags':
    case 'scheduleDaypartOverrides':
    case 'scheduleOverrides':
    case 'scheduleComponentBundles':
      return lang.locations;
    case 'projectConfigs':
    case COLLECTIONS.DAYPART_GLOBAL_COMPONENTS:
      return lang.settings;
    case 'assemblies':
    case 'assemblyComponents':
    case 'assemblyTags':
      return lang.assemblies;
    case 'components':
    case 'componentAssets':
    case 'componentTags':
      return lang.components;
    case 'products':
    case 'productCategories':
    case COLLECTIONS.VARIATION_TEMPLATES: // Dy Templates (Variation Templates) are tracked under 'products' collection
      return lang.products;
    case 'productVariationFlavors':
      return lang.productVariationFlavor;
    case 'productVariationSizes':
      return lang.productVariationType;
    case 'productVariations':
      return lang.productVariations;
    case 'bulkReplaceRequests':
    case 'bulkreplaceComponentSchedules':
      return lang.bulkReplace;
    case 'layouts':
    case 'screens':
    case 'sections':
    case 'screenSections':
    case 'layoutScreens':
      return lang.layouts;
    case COLLECTIONS.SCHEDULE:
    case COLLECTIONS.SCHEDULE_ITEMS:
    case COLLECTIONS.SCHEDULE_ITEM_OVERRIDES:
    case COLLECTIONS.SCHEDULE_ITEM_BACKUP:
    case COLLECTIONS.SCHEDULES:
      return lang.schedules;
    default:
      return `${collection}`;
  }
};

export type narrativeGeneratorsType = {
  [x: string]: {
    [x: string]: Function;
  };
};

export const narrativeGenerators: narrativeGeneratorsType = {
  [COLLECTIONS.PRODUCT_VARIATIONS]: {
    [USER_ACTIONS.CREATE]: (
      productFlavorName: string,
      productVariantName?: string,
      productName?: string,
    ) =>
      `${productFlavorName} was created for ${productName}, ${productVariantName} was created for ${productName}.`,
    [USER_ACTIONS.UPDATE]: (
      productFlavorName: string,
      productVariantName?: string,
      productName?: string,
    ) =>
      `${productFlavorName} was updated for ${productName}, ${productVariantName} was updated for ${productName}.`,
  },
  [COLLECTIONS.SCHEDULE_DAYPARTS]: {
    [USER_ACTIONS.CREATE]: (
      daypartName: string,
      weekdayName: string,
      locationGroupName: string,
    ) => `${daypartName} ${weekdayName} was created on ${locationGroupName}.`,
    [USER_ACTIONS.UPDATE]: (
      daypartName: string,
      weekdayName: string,
      locationGroupName: string,
    ) =>
      `${daypartName} ${weekdayName} schedule was updated for ${locationGroupName}.`,
    [USER_ACTIONS.DELETE]: (
      daypartName: string,
      weekdayName: string,
      locationGroupName: string,
    ) => `${daypartName} ${weekdayName} was deleted from ${locationGroupName}.`,
  },
  [COLLECTIONS.LOCATION_GROUPS]: {
    [USER_ACTIONS.CREATE]: (locationGroupName: string) =>
      `${locationGroupName} was created.`,
  },
  [COLLECTIONS.SCHEDULE_ASSEMBLIES]: {
    [USER_ACTIONS.CREATE]: (
      assemblyName: string,
      daypartName: string,
      locationGroupName: string | null = null,
      locationName: string | null = null,
      locationCode: string | null = null,
    ) => {
      if (locationGroupName !== null)
        return `At ${locationGroupName}, ${assemblyName} was scheduled for ${daypartName} daypart.`;
      else if (locationName !== null && locationCode !== null)
        return `At ${locationName} ${locationCode}, ${assemblyName} was scheduled for ${daypartName} daypart.`;
    },
  },
  [COLLECTIONS.DEFAULT_ASSEMBLIES]: {
    [USER_ACTIONS.CREATE]: (
      assemblyName: string,
      daypartName: string,
      locationGroupName: string | null = null,
      locationName: string | null = null,
      locationCode: string | null = null,
    ) => {
      if (locationGroupName !== null)
        return `At ${locationGroupName}, ${assemblyName} was added to ${daypartName} daypart.`;
      else if (locationName !== null && locationCode !== null)
        return `At ${locationName} ${locationCode}, ${assemblyName} was added to ${daypartName} daypart.`;
    },
    [USER_ACTIONS.DELETE]: (
      assemblyName: string,
      daypartName: string,
      locationGroupName: string | null = null,
    ) => {
      return `At ${locationGroupName}, ${assemblyName} was unscheduled for ${daypartName} daypart.`;
    },
    [USER_ACTIONS.UPDATE]: (
      assemblyName: string,
      oldassemblyName: string,
      daypartName: string,
      locationGroupName: string | null = null,
    ) => {
      return `At ${locationGroupName}, ${oldassemblyName} was replaced by ${assemblyName} for ${daypartName} daypart.`;
    },
  },
  [COLLECTIONS.LOCATIONS]: {
    [USER_ACTIONS.CREATE]: (
      locationName: string,
      locationCode: string,
      locationGroupName: string,
    ) => `${locationName} ${locationCode} was added to ${locationGroupName}.`,
    [USER_ACTIONS.UPDATE]: (
      banner: boolean,
      messaging: boolean,
      hasBannerChanged: boolean,
      hasMessagingChanged: boolean,
      locationCode: string,
    ) => {
      const bannerMsg = banner ? 'on' : 'off';
      const messagingMsg = messaging ? 'on' : 'off';

      if (hasBannerChanged && hasMessagingChanged) {
        if (banner === messaging) {
          return `Banner and Messaging for location ${locationCode} has been turned ${bannerMsg}.`;
        }

        return (
          `Banner for location ${locationCode} has been turned ${bannerMsg}. ` +
          `Messaging for location ${locationCode} has been turned ${messagingMsg}.`
        );
      }

      if (hasBannerChanged) {
        return `Banner for location ${locationCode} has been turned ${bannerMsg}.`;
      }

      if (hasMessagingChanged) {
        return `Messaging for location ${locationCode} has been turned ${messagingMsg}.`;
      }
    },
  },
  [COLLECTIONS.SCHEDULE_DAYPART_OVERRIDES]: {
    [USER_ACTIONS.CREATE]: (
      daypartName: string,
      weekdayName: string,
      locationName: string,
      locationCode: string,
    ) =>
      `${daypartName} ${weekdayName} was created on ${locationName} ${locationCode}.`,
    [USER_ACTIONS.UPDATE]: (
      daypartName: string,
      weekdayName: string,
      locationName: string,
      locationCode: string,
    ) =>
      `${daypartName} ${weekdayName} schedule was updated for ${locationName} ${locationCode}.`,
  },
  [COLLECTIONS.COMPONENTS]: {
    [USER_ACTIONS.CREATE]: (componentName: string) =>
      `${componentName} was created.`,
    [USER_ACTIONS.UPDATE]: (componentName: string) =>
      `${componentName} was updated.`,
    [USER_ACTIONS.DELETE]: (componentName: string) =>
      `${componentName} was deleted.`,
  },
  [COLLECTIONS.PRODUCTS]: {
    [USER_ACTIONS.CREATE]: (productName: string) =>
      `${productName} was created.`,
    [USER_ACTIONS.UPDATE]: (categoryName: string, productGroupName: string) =>
      categoryName
        ? `${categoryName} was added to ${productGroupName}.`
        : `${productGroupName} was updated.`,
    [USER_ACTIONS.DELETE]: (categoryName: string, productGroupName: string) =>
      categoryName
        ? `${productGroupName} was removed from ${categoryName}.`
        : `${productGroupName} was removed.`,
  },
  [COLLECTIONS.PRODUCT_CATEGORIES]: {
    [USER_ACTIONS.CREATE]: (productCategoryName: string) =>
      `${productCategoryName} was created.`,
    [USER_ACTIONS.UPDATE]: (productCategoryName: string) =>
      `${productCategoryName} was updated.`,
  },
  [COLLECTIONS.ASSEMBLIES]: {
    [USER_ACTIONS.CREATE]: (assemblyName: string) =>
      `${assemblyName} was created.`,
    [USER_ACTIONS.UPDATE]: (assemblyName: string) =>
      `${assemblyName} was updated.`,
    [USER_ACTIONS.DELETE]: (assemblyName: string) =>
      `${assemblyName} was deleted.`,
  },
  [COLLECTIONS.ASSEMBLIES_COMPONENTS]: {
    [USER_ACTIONS.CREATE]: (assemblyName: string, componentName: string) =>
      `${componentName} was added to ${assemblyName}.`,
    [USER_ACTIONS.UPDATE]: (assemblyName: string, componentName: string) =>
      `${componentName} was updated for ${assemblyName}.`,
    [USER_ACTIONS.DELETE]: (assemblyName: string, componentName: string) =>
      `${componentName} was removed from ${assemblyName}.`,
  },
  [COLLECTIONS.BULK_REPLACEMENT]: {
    [USER_ACTIONS.CREATE]: (replacementName: string) =>
      `${replacementName} was added.`,
    [USER_ACTIONS.UPDATE]: (replacementName: string) =>
      `${replacementName} was updated.`,
    [USER_ACTIONS.DELETE]: (replacementName: string) =>
      `${replacementName} was deleted.`,
  },
  [COLLECTIONS.PROJECT_CONFIGS]: {
    [USER_ACTIONS.UPDATE]: (fieldName: string) =>
      `${fieldName} settings was updated on Global Configurations.`,
  },
  [COLLECTIONS.THEMES]: {
    [USER_ACTIONS.UPDATE]: (fieldName: string) => {
      const channelNames = [
        lang.indoor,
        lang.outdoor,
        lang.mcCafe,
        lang.digitalMerchandiser,
      ];
      if (channelNames.includes(fieldName)) {
        return `${lang.defaultThemesNarrativeMessage(fieldName)}`;
      }

      return `${fieldName} settings was updated on Accessibility settings.`;
    },
  },
  [COLLECTIONS.ASSETS]: {
    [USER_ACTIONS.UPDATE]: (assetName: string) => `${assetName} was updated.`,
  },
  [COLLECTIONS.SCHEDULE_COMPONENTS]: {
    [USER_ACTIONS.CREATE]: (
      locationGroupName: string,
      scheduleName: string,
      componentName: string[],
    ) =>
      `At ${locationGroupName},${scheduleName} was created.${componentName} ${
        componentName.length > 1 ? 'were' : 'was'
      } scheduled.`,
    [USER_ACTIONS.UPDATE]: (
      locationGroupName: string,
      scheduleName: string,
      componentName: string[],
    ) =>
      `At ${locationGroupName},${scheduleName} was updated.${componentName} ${
        componentName.length > 1 ? 'were' : 'was'
      } scheduled.`,
    [USER_ACTIONS.DELETE]: (locationGroupName: string, scheduleName: string) =>
      `At ${locationGroupName}, ${scheduleName} was deleted.`,
  },
  [COLLECTIONS.SCHEDULE_COMPONENTS_WITH_LGS]: {
    [USER_ACTIONS.CREATE]: (locationGroupName: string, scheduleName: string) =>
      `At ${locationGroupName}, ${scheduleName} was created.`,
    [USER_ACTIONS.UPDATE]: (locationGroupName: string, scheduleName: string) =>
      `At ${locationGroupName}, ${scheduleName} was updated.`,
  },
  [COLLECTIONS.SCHEDULE_ITEMS]: {
    [USER_ACTIONS.DELETE]: (
      locationGroupName: string,
      componentName: string,
      scheduleName: string,
    ) =>
      `At ${locationGroupName},${componentName} was deleted from ${scheduleName}.`,
    [USER_ACTIONS.UPDATE]: (
      componentName: string,
      replacementName: string,
      scheduleId: number,
    ) =>
      `${componentName} was replaced by ${replacementName} for ${scheduleId}`,
  },
  [COLLECTIONS.DAYPART_GLOBAL_COMPONENTS]: {
    [USER_ACTIONS.UPDATE]: (
      eventName: string,
      daypartName: string,
      componentName: string,
    ) => `${eventName}: ${daypartName} was assigned to ${componentName}.`,
    [USER_ACTIONS.DELETE]: (
      eventName: string,
      daypartName: string,
      componentName: string,
    ) => `${eventName}: ${daypartName} was removed from ${componentName}.`,
  },
  [COLLECTIONS.GLOBAL_COMPONENTS_SCREENS]: {
    [USER_ACTIONS.UPDATE]: () => 'Screen configuration was updated',
  },
  [COLLECTIONS.VARIATION_TEMPLATES]: {
    [USER_ACTIONS.CREATE]: (productVariationName: string) =>
      `DY display under ${productVariationName} was created`,
    [USER_ACTIONS.UPDATE]: (productVariationName: string) =>
      `DY display under ${productVariationName} was updated`,
    [USER_ACTIONS.DELETE]: (productVariationName: string) =>
      `DY display under ${productVariationName} was deleted`,
  },
};
